import { useEffect, useState } from 'react'
import { fetchControlSummary } from '../api/control'
import { fetchOutcomeMetrics } from '../api/metrics'
import HomeOutcomePerformancePanel from '../components/HomeOutcomePerformancePanel'
import HomeInsightCard from '../components/HomeInsightCard'
import InsightDetailModal from '../components/InsightDetailModal'
import styles from './dashboard.module.css'

/**
 * Dashboard ("Home") — the control panel overview: an executive-summary
 * banner and a grid of insight cards. Everything below the page-level fetch
 * is data-driven (see BlockRenderer) — this component only owns the
 * page-level fetch and which card's detail modal (if any) is open.
 */
export default function Dashboard() {
  const [summary, setSummary] = useState(null)
  const [outcomeMetrics, setOutcomeMetrics] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [viewingCardId, setViewingCardId] = useState(null)

  useEffect(() => {
    let active = true
    Promise.all([fetchControlSummary(), fetchOutcomeMetrics()])
      .then(([d, m]) => { if (active) { setSummary(d); setOutcomeMetrics(m) } })
      .catch(e => { if (active) setError(e.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  const viewingCard = summary?.cards.find(c => c.id === viewingCardId) ?? null

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Execution Intelligence Overview</h1>
          <p className={styles.pageSub}>Surface the hidden execution dynamics behind your operational KPIs.</p>
        </div>
        <div className={styles.skeletonBannerRow}>
          <div className={styles.skeletonBanner} />
          <div className={styles.skeletonBanner} />
        </div>
        <div className={styles.skeletonGrid}>
          {Array(4).fill(0).map((_, i) => <div key={i} className={styles.skeletonBigCard} />)}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>Failed to load the control panel: {error}</div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Execution Intelligence Overview</h1>
        <p className={styles.pageSub}>Surface the hidden execution dynamics behind your operational KPIs.</p>
      </div>

      <div className={styles.banner}>
        <HomeOutcomePerformancePanel metrics={outcomeMetrics} />
        {/* Placeholder for initial release — see HomeEfficiencyGainPanel for
            the real composite-score panel this slot will hold once ready. */}
        <div className={styles.comingSoonPanel}>
          <span className={styles.comingSoonText}>Coming Soon</span>
        </div>
      </div>

      {summary.cards.length === 0 ? (
        <div className={styles.emptyState}>Execution insights will appear here.</div>
      ) : (
        <div className={styles.grid}>
          {summary.cards.map(card => (
            <HomeInsightCard
              key={card.id}
              rank={card.rank}
              accent={card.accent}
              title={card.title}
              subtitle={card.subtitle}
              pending={card.pending}
              hero={card.hero}
              onViewDetails={() => setViewingCardId(card.id)}
            />
          ))}
          {/* The grid always reads as 4 columns (see .grid > * in
              dashboard.module.css) — these fill out any columns short of 4
              so it's visually obvious more cards belong there, not that the
              layout is just sparse. */}
          {Array.from({ length: Math.max(0, 4 - summary.cards.length) }).map((_, i) => (
            <div key={`slot-${i}`} className={styles.cardSlotPlaceholder} aria-hidden="true" />
          ))}
        </div>
      )}

      {viewingCard && (
        <InsightDetailModal
          key={viewingCard.id}
          card={viewingCard}
          onClose={() => setViewingCardId(null)}
        />
      )}
    </div>
  )
}
